// ═══════════════════════════════════════════════════════════════════════════════════════════
// ClalMarkIcon — Clal's own mark, for the one sidebar entry that belongs to Clal.
//
// WHY. `routes.tsx` gave "מרכז כלל" and "אינטגרציות" the SAME icon —
// `MdOutlineDashboardCustomize`, the four-squares-plus grid — and the two entries are direct
// neighbours in the menu. Two adjacent rows with one glyph between them is not a small polish
// item: the icon is the only thing a collapsed sidebar shows at all.
//
// WHERE THIS PATH CAME FROM. It is Clal's OWN vector, lifted unmodified (bar a uniform scale)
// out of their header logo: https://www.clalbit.co.il/AngularClient/dist/assets/images/header/
// logo-eng-heb.svg, a 207×26 wordmark of eight paths. Seven are `fill="#262B3A"` — the
// letterforms of כלל and CLAL. The EIGHTH, path 4, is `fill="#3D6CED"` and is the mark itself:
// one closed path of twelve cubic Béziers, spanning x 75.457..112.528 and y 2.369..26.002 in the
// wordmark's own coordinates. #3D6CED is `--clal-blue`, כחול דיגיטלי, one of the ten official
// hexes, so the file is the brand asset and the blue path in it is the brand mark.
//
// CORRECTION, and it is worth recording because it cost real work: the first version of this file
// carried a 56-point POLYGON traced out of https://www.clalbit.co.il/favicon.ico, and a comment
// asserting that Clal publish no compact vector mark at all. That assertion was wrong. It came
// from searching for a STANDALONE file — logo.svg, logo-icon.svg, favicon.svg, all genuinely 404
// under their header assets folder — and never opening the wordmark to see what was inside it.
// The R4 design panel caught it. The traced polygon rendered indistinguishably from this path at
// 28px, 56px and 150px, so nothing was visibly wrong; it was simply 603 characters of straight
// lines standing in for 399 characters of the real curves.
//
// VERIFICATION RUN, not assumed:
//   · true bbox by sampling all twelve cubics at 1/512 — control points overshoot the endpoints,
//     so the endpoint extremes are not the bbox: w 37.071, h 23.633, aspect 1.569.
//   · the favicon's own ink measures 256×164, aspect 1.561, and its 30,313 ink pixels are
//     #3D6CED. Same mark, two deliveries, 0.5% apart on aspect.
//   · rendered side by side against the favicon PNG at 28 / 56 / 150 px: indistinguishable.
//
// NORMALISATION. Scaled to 21 of 24 units wide and centred, giving x 1.5..22.5, y 5.58..18.46.
// 21 and not 24 because the Material icons around it span roughly 3..21 of their own 24 box;
// matching their optical size is what stops this one — which is WIDE AND SHORT — from reading as
// oversized next to its neighbours. The traced version it replaces occupied x 1.5..22.42,
// y 5.27..18.64, so the swap does not move the sidebar's geometry.
//
// `fill="currentColor"` and no hard-coded colour: the sidebar paints it white on the menu's red,
// and anywhere else it takes the ink of its context. This is the ONE Clal asset inside the admin
// chrome, and it is here because an identity mark labels a section — it is not a control. Buttons,
// fields and dialogs on the ClalCenter screen stay Pulseem's (owner, 08.09.2026); see
// ItemDrawer.tsx. If Clal ever ship a dedicated icon asset, replace the constant below with it
// rather than re-deriving anything.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React from 'react';

const CLAL_MARK = 'M16.08 11.95C16.08 10.44 19.7 10.12 22.5 10.26C22.5 9.05 22.48 7.03 22.5 5.58C18.02 4.75 13.41 5.76 12 9.47C10.59 5.76 5.98 4.75 1.5 5.58C1.52 7.04 1.5 9.05 1.5 10.26C4.3 10.12 7.92 10.44 7.92 11.95C7.92 13.56 4.13 13.59 1.5 13.56C1.5 14.77 1.52 17.01 1.5 18.46C5.98 19.26 10.59 18.04 12 14.25C13.41 18.04 18.02 19.26 22.5 18.46C22.48 17.01 22.5 14.77 22.5 13.56C19.87 13.59 16.08 13.56 16.08 11.95Z';

interface Props {
    /** Matches the `size` the other sidebar icons are given. 28 is the family default. */
    size?: number;
}

const ClalMarkIcon = ({ size = 28 }: Props) => (
    <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
    >
        <path d={CLAL_MARK} />
    </svg>
);

export default ClalMarkIcon;
